import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "../common/Select";
import ALGridReport from "./ALGridReport";
import ALNoteReport from "./ALNoteReport";
import {
  getAllTeamClients,
  getTeamClients,
  getWeeklyReport,
} from "../services/soar";
import ReportsExpand from "../common/ReportsExpand";

const ALWeeklyReport = ({ ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reportParams, setReportParams] = useState(
    location.state?.reportParams || {
      Division: "",
      Client: "",
      SelectedWeek: "",
    }
  );
  const [clientOptions, setClientOptions] = useState(
    location.state?.clientOptions || []
  );
  const [reportData, setReportData] = useState(location.state?.reportData);
  const [loadStatus, setLoadStatus] = useState({});
  const [controllerSignal, setControllerSignal] = useState();

  const clientName = useMemo(() => {
    if (!reportParams.Client) {
      return;
    }

    const option = clientOptions.filter(
      (o) => o._id === reportParams.Client
    )[0];
    return option.name;
  }, [reportParams.Client, clientOptions]);

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const mainElement = document.getElementById("main");

    if (mainElement) {
      if (reportData) {
        mainElement.className = "container-fluid";
      }

      return () => (mainElement.className = "container");
    }
  }, [reportData]);

  const getSundays = () => {
    const options = [];
    let sunday = -63;

    while (sunday <= 0) {
      const sundayMoment = moment().day(sunday);

      options.push({
        _id: sundayMoment.format("L"),
        name: sundayMoment.format("L"),
      });
      sunday = sunday + 7;
    }

    return options;
  };

  const datesOfTheWeek = useMemo(() => {
    const dates = [];

    for (let index = 0; index < 7; index++) {
      const date = moment(reportParams.SelectedWeek)
        .add(index, "d")
        .format(moment.HTML5_FMT.DATE);
      dates.push(date);
    }

    return dates;
  }, [reportParams.SelectedWeek]);

  const handleChange = ({ currentTarget: input }) => {
    setReportParams((r) => ({
      ...r,
      [input.name]: input.type === "checkbox" ? input.checked : input.value,
    }));
  };

  const getClientsByDivision = async (division, showDischarged) => {
    if (!division) {
      setClientOptions([]);
      return;
    }

    setLoadStatus((ls) => ({ ...ls, loadingClients: true }));
    if (showDischarged) {
      const response = await getAllTeamClients([division]);
      if (!controllerSignal.aborted) {
        if (response) {
          const options = response.map((contact) => ({
            _id: contact.contactid,
            name: contact.fullname,
          }));
          setClientOptions([
            {
              _id: "31238747-F411-EF11-B6A2-00224830E7E1",
              name: "Testing, Portal",
            },
            ...options,
          ]);
        }

        setLoadStatus((ls) => ({
          ...ls,
          loadingClients: false,
          clientsLoaded: true,
        }));
      }
    } else {
      const response = await getTeamClients([division]);
      if (!controllerSignal.aborted) {
        if (response) {
          const options = response.map((contact) => ({
            _id: contact.contactid,
            name: contact.fullname,
          }));
          setClientOptions([
            {
              _id: "31238747-F411-EF11-B6A2-00224830E7E1",
              name: "Testing, Portal",
            },
            ...options,
          ]);
        }

        setLoadStatus((ls) => ({
          ...ls,
          loadingClients: false,
          clientsLoaded: true,
        }));
      }
    }
  };

  const generateReport = useCallback(async () => {
    setLoadStatus((ls) => ({ ...ls, loadingReport: true }));
    const beginDate = moment(reportParams.SelectedWeek).format("YYYY-MM-DD");
    const endDate = moment(reportParams.SelectedWeek)
      .add(6, "days")
      .format("YYYY-MM-DD");
    const response = await getWeeklyReport(
      reportParams.Client,
      beginDate,
      endDate,
      controllerSignal
    );

    if (!controllerSignal.aborted) {
      if (response) {
        const mainElement = document.getElementById("main");

        if (mainElement) {
          mainElement.className = "container-fluid";
        }

        const groups = ["ServiceDate", "SkillCategory"];
        const grouped = {};

        response.forEach(function (a) {
          groups
            .reduce(function (o, g, i) {
              // take existing object,
              o[a[g]] = o[a[g]] || (i + 1 === groups.length ? [] : {}); // or generate new obj, or
              return o[a[g]]; // at last, then an array
            }, grouped)
            .push(a);
        });

        const skillCategories = [
          "Personal Hygiene",
          "House Keeping",
          "Meal Preparation",
          "Shopping",
          "Laundry",
          "Money Management",
          "Healthy Lifestyle",
          "Stress Management",
          "Use of Public Transportation",
          "Medication Management",
          "Behavior Education",
        ];

        datesOfTheWeek.forEach((d) => {
          const servicesByDate = grouped[d];
          if (servicesByDate) {
            let dailyUnits = 0;
            skillCategories.forEach((category) => {
              if (servicesByDate[category]) {
                const units = [];
                const settings = [];
                const progress = [];
                servicesByDate[category].forEach((service) => {
                  dailyUnits += service.UnitsForService;
                  units.push(service.UnitsForService);
                  settings.push(service.ServiceSettingValue);
                  progress.push(service.ChildsProgress);
                });
                servicesByDate[`${category}.units`] = units.join(", ");
                servicesByDate[`${category}.settings`] = settings.join(", ");
                servicesByDate[`${category}.progress`] = progress.join(", ");
              }
            });
            servicesByDate.totalUnits = dailyUnits;
          }
        });

        setReportData(grouped);
        navigate("./ALWeeklyReport", {
          state: {
            reportData: grouped,
            reportParams: reportParams,
            clientOptions: clientOptions,
          },
        });
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }

      setLoadStatus((ls) => ({
        ...ls,
        loadingReport: false,
        reportLoaded: true,
      }));
    }
  }, [reportParams, datesOfTheWeek, clientOptions, controllerSignal, navigate]);

  const getDivisionOptions = (safyInfo) => {
    if (!safyInfo) {
      return [];
    }

    const parsedInfo = JSON.parse(safyInfo);

    if (!parsedInfo?.user) {
      return [];
    }

    const divisionAssignments = parsedInfo.user.safyInfo.divisions.filter(
      (d) => d.Active === "1" && d.CompanyName === "SAFY of Alabama"
    );
    const options = divisionAssignments.map((d) => ({
      _id: d.DivisionName,
      name: d.DivisionName,
    }));
    return options;
  };

  return !reportData ? (
    <ReportsExpand title="AL Weekly Report" {...rest}>
      <div className="row">
        <Select
          className="col-2"
          name="Division"
          label="Division"
          options={getDivisionOptions(localStorage.getItem("safyInfo"))}
          value={reportParams.Division}
          onChange={({ currentTarget: input }) => {
            handleChange({ currentTarget: input });
            getClientsByDivision(input.value, reportParams.ShowDischarged);
          }}
        />
        <div
          className="col-2 form-check form-switch"
          style={{ marginTop: "38px" }}
        >
          <input
            name="ShowDischarged"
            className="form-check-input"
            type="checkbox"
            id="ShowDischarged"
            checked={reportParams.ShowDischarged}
            onChange={({ currentTarget: input }) => {
              handleChange({ currentTarget: input });
              getClientsByDivision(reportParams.Division, input.checked);
            }}
          />
          <label className="form-check-label" htmlFor="ShowDischarged">
            Show Discharged
          </label>
        </div>
        <Select
          className="col-2"
          name="Client"
          label="Client"
          options={clientOptions}
          value={reportParams.Client}
          onChange={handleChange}
          disabled={!loadStatus.clientsLoaded || loadStatus.loadingClients}
        />
        <div className="col-2">
          <Select
            name="SelectedWeek"
            label="Choose a Sunday:"
            value={reportParams.SelectedWeek}
            options={getSundays()}
            onChange={handleChange}
          />
        </div>
        {loadStatus.reportLoaded && !reportData ? (
          <div className="col-2 alert alert-warning">No notes found...</div>
        ) : null}
        {reportParams.Client && reportParams.SelectedWeek ? (
          <div className="col-2">
            <i
              className="fas fa-arrow-circle-right fa-3x float-end"
              onClick={generateReport}
            ></i>
          </div>
        ) : null}
      </div>
    </ReportsExpand>
  ) : (
    <>
      <div className="row">
        <div className="col">
          <div className="w-100 text-center">
            <div className="h5">BASIC LIVING SKILLS REPORT</div>
            <div className="fw-bold">
              (Include each category of Basic Living Skills provided in the
              daily Progress Report)
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-3">
          <div className="d-flex mb-2">
            <div className="me-2 fw-bold">Child's Name:</div>
            <div className="border-bottom border-dark flex-fill">
              {clientName}
            </div>
          </div>
        </div>
        <div className="col"></div>
        <div className="col-3">
          <div className="d-flex mb-2">
            <div className="me-2 fw-bold">Case Number: </div>
            <div className="border-bottom border-dark flex-fill">
              {/* {reportData.CaseNumber}    */}
            </div>
          </div>
        </div>
      </div>
      <ALGridReport reportData={reportData} datesOfTheWeek={datesOfTheWeek} />
      <ALNoteReport reportData={reportData} datesOfTheWeek={datesOfTheWeek} />
    </>
  );
};

export default ALWeeklyReport;
